import { createSlice } from '@reduxjs/toolkit';
import {
  fetchUserNotifications,
  sendUserNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  getUnreadNotificationCount
} from './notificationsActionThunks';

const initialState = {
  notifications: {}, // Store notifications by userId: { userId: [notifications] }
  unreadCounts: {}, // Store unread counts by userId: { userId: count }
  loading: false,
  error: null,
};

const userNotificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch User Notifications
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, { payload }) => {
        const { userId, notifications } = payload;
        state.notifications[userId] = notifications;
        state.loading = false;
      })
      .addCase(fetchUserNotifications.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Send Notification
    builder
      .addCase(sendUserNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendUserNotification.fulfilled, (state, { payload }) => {
        const { user_id, ...notification } = payload;
        if (!state.notifications[user_id]) {
          state.notifications[user_id] = [];
        }
        state.notifications[user_id].push(notification);
        // Update unread count
        if (state.unreadCounts[user_id] !== undefined) {
          state.unreadCounts[user_id] += 1;
        }
        state.loading = false;
      })
      .addCase(sendUserNotification.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Mark Notification as Read
    builder
      .addCase(markNotificationAsRead.pending, (state) => {
        // Don't set loading to true for individual mark as read to avoid UI blocking
        state.error = null;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, { payload }) => {
        const { userId, notificationId } = payload;
        if (state.notifications[userId]) {
          const notification = state.notifications[userId].find(n => n.id === notificationId);
          if (notification) {
            // Handle both property naming conventions
            const wasUnread = notification.is_read === false || notification.unread === true;
            
            // Update the notification state
            notification.is_read = true;
            if (notification.hasOwnProperty('unread')) {
              notification.unread = false;
            }
            
            // Update unread count only if the notification was actually unread
            if (wasUnread && state.unreadCounts[userId] !== undefined && state.unreadCounts[userId] > 0) {
              state.unreadCounts[userId] -= 1;
            }
          }
        }
        state.loading = false;
      })
      .addCase(markNotificationAsRead.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // Mark All Notifications as Read
    builder
      .addCase(markAllNotificationsAsRead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, { payload }) => {
        const { userId } = payload;
        if (state.notifications[userId]) {
          // Update all notifications to be read
          state.notifications[userId].forEach(notification => {
            notification.is_read = true;
            if (notification.hasOwnProperty('unread')) {
              notification.unread = false;
            }
          });
          // Set unread count to 0
          state.unreadCounts[userId] = 0;
        }
        state.loading = false;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
      
    // Get Unread Notification Count
    builder
      .addCase(getUnreadNotificationCount.pending, (state) => {
        // Don't set loading to true for count fetch to avoid UI blocking
        state.error = null;
      })
      .addCase(getUnreadNotificationCount.fulfilled, (state, { payload }) => {
        const { userId, unreadCount } = payload;
        state.unreadCounts[userId] = unreadCount;
        state.loading = false;
      })
      .addCase(getUnreadNotificationCount.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export const { clearError } = userNotificationsSlice.actions;

export default userNotificationsSlice.reducer;